import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { PrimaryTrade, TradeCategory } from 'src/app/classes/models';
import { ToastService } from 'src/app/services/toast.service';
import { TradeService } from 'src/app/services/trade.service';

@Component({
  selector: 'app-trade-selection-dropdown',
  templateUrl: './trade-selection-dropdown.component.html',
  styleUrl: './trade-selection-dropdown.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TradeSelectionDropdownComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private TradeService: TradeService
  ) {}

  @ViewChild('TradeCategoryDiv') TradeCategoryDiv?: ElementRef<HTMLDivElement>;

  TradeCategories: Set<TradeCategory> = new Set([]);
  FilteredTradeCategories: TradeCategory[] = [];

  // Contains selected trades & trade categories, which are then applied to the grid as a filter.
  SelectedTradesAndCategories: {
    Trades: PrimaryTrade[];
  } = {
    Trades: []
  };

  // Used when filtering by trades.
  SearchModel: {
    trade: string;
  } = {
    trade: ''
  };

  // Because we're utilizing references to update trade data during search,
  // we need to store filtered trades, otherwise they will be permanently removed
  // from the selection set.
  StoredTradeMap = new Map<TradeCategory, PrimaryTrade[]>([]);

  // Stores the original relationship between the trade and the trade category.
  // If a selected trade is deselected, we can reference this map to quickly insert
  // it back into the applicable category. This may need to be refactored if we allow
  // the selection of trade categories as well as trades.
  TradeToTradeCategoryMap = new Map<PrimaryTrade, TradeCategory>([]);

  // When a trade is selected or deselected, sends the selections as an event to parent component.
  @Output() tradeSelectionEvent = new EventEmitter<{
    Trades: PrimaryTrade[];
  }>();
  expandedCategoryIds = new Set<number>();

  sortTradeCategories(TradeCategories: TradeCategory[]) {
    TradeCategories.sort((a, b) => {
      if (a.categoryName! < b.categoryName!) {
        return -1;
      }

      return 1;
    });
  }

  sortTrades(Trades: PrimaryTrade[]) {
    Trades.sort((a, b) => {
      if (a.name! < b.name!) {
        return -1;
      }

      return 1;
    });
  }

  clearSearchText() {
    (this.TradeCategoryDiv?.nativeElement as HTMLDivElement).textContent = '';
    Object.assign(this.SearchModel, { trade: '' });
  }

  isTradeSelected(trade: PrimaryTrade): boolean {
    return this.SelectedTradesAndCategories.Trades.some(
      (t) => t.id === trade.id
    );
  }

  toggleCategoryExpanded(categoryId: number | undefined | null): void {
    if (categoryId === null || categoryId === undefined) {
      return;
    }
    if (this.expandedCategoryIds.has(categoryId)) {
      this.expandedCategoryIds.delete(categoryId);
    } else {
      this.expandedCategoryIds.add(categoryId);
    }
    this.expandedCategoryIds = new Set(this.expandedCategoryIds);
  }

  isCategoryExpanded(categoryId: number | undefined | null): boolean {
    return categoryId != null && this.expandedCategoryIds.has(categoryId);
  }

  toggleTrade(trade: PrimaryTrade, tradeCategory: TradeCategory): void {
    if (this.isTradeSelected(trade)) {
      const index = this.SelectedTradesAndCategories.Trades.findIndex(
        (t) => t.id === trade.id
      );
      if (index !== -1) {
        this.deselectTrade(trade, index);
      }
    } else {
      this.selectTrade(trade, tradeCategory);
    }
  }

  selectTrade(Trade: PrimaryTrade, TradeCategory: TradeCategory) {
    this.reapplyStoredTrades();

    const { PrimaryTrades } = TradeCategory;

    if (PrimaryTrades) {
      const TradeIndex = PrimaryTrades.findIndex(
        (FoundTrade) => FoundTrade === Trade
      );
      PrimaryTrades.splice(TradeIndex, 1);
    }

    const { Trades } = this.SelectedTradesAndCategories;

    Trades.push(Trade);
    this.clearSearchText();
    this.FilteredTradeCategories = Array.from(this.TradeCategories);

    this.sortTrades(PrimaryTrades || []);
    this.sortTradeCategories(this.FilteredTradeCategories);

    this.tradeSelectionEvent.emit(this.SelectedTradesAndCategories);
  }

  deselectTrade(Trade: PrimaryTrade, index: number) {
    const FoundTradeCategory = this.TradeToTradeCategoryMap.get(Trade);
    if (!FoundTradeCategory) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error deselecting the trade.',
        { returnError: true }
      );
    }

    this.SelectedTradesAndCategories['Trades'].splice(index, 1);
    const { PrimaryTrades } = FoundTradeCategory;

    if (!PrimaryTrades) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Trade category is missing trades.',
        { returnError: true }
      );
    }

    PrimaryTrades.push(Trade);
    this.sortTrades(PrimaryTrades);
    this.sortTradeCategories(this.FilteredTradeCategories);

    this.tradeSelectionEvent.emit(this.SelectedTradesAndCategories);
  }

  reapplyStoredTrades() {
    this.StoredTradeMap.forEach((Trades, TradeCategory) => {
      const { PrimaryTrades } = TradeCategory;
      if (PrimaryTrades) {
        PrimaryTrades.push(...Trades);
      }
    });

    this.StoredTradeMap.clear();
  }

  filterTradesAndCategories(event: Event) {
    const { textContent: textContentRaw } = event.target as HTMLDivElement;
    // Replace &nbsp from textContent if found.
    const textContent = textContentRaw?.replace(
      new RegExp(String.fromCharCode(160), 'g'),
      ''
    );

    Object.assign(this.SearchModel, { trade: textContent || '' });

    this.reapplyStoredTrades();

    this.FilteredTradeCategories = Array.from(this.TradeCategories).reduce(
      (accumulativeValue, TradeCategory) => {
        const { categoryName, PrimaryTrades } = TradeCategory;

        if (!PrimaryTrades) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Trade category is missing trade information.',
            { returnError: true }
          );
        }

        TradeCategory.PrimaryTrades = PrimaryTrades.reduce(
          (accumulativeValue, Trade) => {
            const { name } = Trade;

            if (
              (name &&
                textContent &&
                name.toLowerCase().includes(textContent.toLowerCase())) ||
              !textContent
            ) {
              accumulativeValue.push(Trade);
            } else {
              const StoredTrades = this.StoredTradeMap.get(TradeCategory) || [];
              StoredTrades.push(Trade);
              if (!this.StoredTradeMap.has(TradeCategory)) {
                this.StoredTradeMap.set(TradeCategory, StoredTrades);
              }
            }

            return accumulativeValue;
          },
          [] as PrimaryTrade[]
        );

        if (
          !textContent ||
          TradeCategory.PrimaryTrades.length > 0 ||
          (categoryName &&
            textContent &&
            categoryName.toLowerCase().includes(textContent.toLowerCase()))
        ) {
          accumulativeValue.push(TradeCategory);
        }

        return accumulativeValue;
      },
      [] as TradeCategory[]
    );

    this.sortTradeCategories(this.FilteredTradeCategories);
  }

  ngOnInit(): void {
    this.TradeService.getTradeCategories({
      PrimaryTrade: JSON.stringify({ options: { required: true } })
    }).subscribe({
      next: (TradeCategories) => {
        if (TradeCategories) {
          TradeCategories.forEach((TradeCategory) => {
            this.TradeCategories.add(TradeCategory);
            this.FilteredTradeCategories.push(TradeCategory);

            const { PrimaryTrades } = TradeCategory;
            if (PrimaryTrades) {
              this.sortTrades(PrimaryTrades);
              PrimaryTrades.forEach((Trade) => {
                this.TradeToTradeCategoryMap.set(Trade, TradeCategory);
              });
            }
          });
          this.sortTradeCategories(this.FilteredTradeCategories);
          // Expand first category by default.
          const firstId = this.FilteredTradeCategories[0]?.id;
          if (firstId !== null && firstId !== undefined) {
            this.expandedCategoryIds.add(firstId);
          }
        }
      }
    });
  }
}
